import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BankIdLayout } from '../components/BankIdLayout';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { authenticatedHomePath } from '../utils/onboarding';

export function VerifyEmail() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const token = params.get('token') || '';
  const verification = useRef<{ token: string; request: Promise<{ verified: boolean }> } | null>(null);
  const [state, setState] = useState<'idle' | 'verifying' | 'verified' | 'invalid'>(token ? 'verifying' : 'idle');
  const [resend, setResend] = useState<'idle' | 'busy' | 'sent' | 'error'>('idle');
  useEffect(() => {
    if (!token) return;
    let disposed = false;
    setState('verifying');
    if (verification.current?.token !== token) verification.current = { token, request: authApi.verifyEmail(token) };
    void verification.current.request.then(async () => {
      if (disposed) return;
      // This endpoint confirms contact data; only an existing BankID session can be refreshed.
      await refreshUser();
      if (!disposed) setState('verified');
    }).catch(() => { if (!disposed) setState('invalid'); });
    return () => { disposed = true; };
  }, [token, refreshUser]);
  const home = authenticatedHomePath(user);
  const invitationId = sessionStorage.getItem('pendingInvitation');
  const destination = isAuthenticated && invitationId && home !== '/onboarding' && home !== '/verify-email'
    ? `/invitations/${encodeURIComponent(invitationId)}/decision` : home;
  const title = state === 'verified' ? 'auth.verifyEmail.verifiedTitle' : state === 'invalid' ? 'auth.verifyEmail.invalidTitle' : 'auth.verifyEmail.title';
  return <BankIdLayout>
    <h1 className="h3 text-center">{t(title)}</h1>
    {state === 'verifying' ? <p role="status">{t('auth.verifyEmail.verifying')}</p> : <>
      <p className={state === 'invalid' ? 'alert alert-danger' : 'text-muted'} role="status">{t(state === 'verified' ? 'auth.verifyEmail.verifiedDescription' : state === 'invalid' ? 'auth.verifyEmail.invalidDescription' : 'auth.bankid.emailOnly')}</p>
      {isAuthenticated ? <>
        <Link to={destination} className="btn btn-primary bankid-action w-100 justify-content-center">{t('auth.verifyEmail.continue')}</Link>
        {state !== 'verified' && <button type="button" className="btn btn-link mx-auto mt-3" disabled={resend === 'busy'} onClick={async () => {
          setResend('busy');
          try { await authApi.resendVerificationEmail(); setResend('sent'); }
          catch { setResend('error'); }
        }}>{t('auth.verifyEmail.resend')}</button>}
      </> : <Link to="/login" className="btn btn-primary bankid-action w-100 justify-content-center">{t('auth.bankid.loginAgain')}</Link>}
      {resend === 'sent' && <p className="alert alert-success mt-3" role="status">{t('auth.verifyEmail.resendSent')}</p>}
      {resend === 'error' && <p className="alert alert-danger mt-3" role="alert">{t('common.error')}</p>}
    </>}
  </BankIdLayout>;
}
